export type ParentPortalModuleId =
  | "dashboard"
  | "applications"
  | "enrollment"
  | "financial-aid"
  | "calendar"
  | "tuition"
  | "attendance"
  | "academic"
  | "resources";

export type ParentPortalModuleAvailability = {
  navIds: ParentPortalModuleId[];
  disabledIds: ParentPortalModuleId[];
  descopedIds: ParentPortalModuleId[];
};

export type ParentPortalModuleGateStatus =
  | "unmatched"
  | "pending"
  | "enabled"
  | "disabled";

/**
 * Shell-level route gate for parent modules.
 *
 * Parent module content stays unmounted while settings are loading, and disabled
 * module routes resolve to the first visible parent module without rendering the
 * disabled module body or its route-specific access gate.
 */
export type ParentPortalModuleGateState = {
  status: ParentPortalModuleGateStatus;
  moduleId: ParentPortalModuleId | null;
  fallbackPath: string | null;
  shouldRenderModuleContent: boolean;
};

export const ENGINEERING_AVAILABLE_PARENT_MODULE_IDS = [
  "dashboard",
  "applications",
  "financial-aid",
] as const satisfies ReadonlyArray<ParentPortalModuleId>;

const ALL_PARENT_MODULE_IDS = [
  "dashboard",
  "applications",
  "enrollment",
  "financial-aid",
  "calendar",
  "tuition",
  "attendance",
  "academic",
  "resources",
] as const satisfies ReadonlyArray<ParentPortalModuleId>;

const PARENT_MODULE_ROUTE_MAP: Record<ParentPortalModuleId, string> = {
  dashboard: "/parent/portal/dashboard",
  applications: "/parent/portal/applications",
  enrollment: "/parent/portal/enrollment",
  "financial-aid": "/parent/portal/financialaid",
  calendar: "/parent/portal/calendar",
  tuition: "/parent/portal/tuition",
  attendance: "/parent/portal/attendance",
  academic: "/parent/portal/academic",
  resources: "/parent/portal/resources",
};

const buildParentModuleRouteAliases = (): Record<
  ParentPortalModuleId,
  readonly string[]
> => {
  const aliases = {} as Record<ParentPortalModuleId, readonly string[]>;
  for (const id of ALL_PARENT_MODULE_IDS) {
    const route = PARENT_MODULE_ROUTE_MAP[id];
    const alias = route.replace("/parent/portal/", "/parent/portal--new/");
    aliases[id] =
      id === "financial-aid"
        ? [
            "/parent/portal/financial-aid",
            alias,
            "/parent/portal--new/financial-aid",
          ]
        : [alias];
  }
  return aliases;
};

const PARENT_MODULE_ROUTE_ALIASES = buildParentModuleRouteAliases();

const normalizeModuleId = (value: unknown): ParentPortalModuleId | null => {
  if (typeof value !== "string") {
    return null;
  }
  const normalized = value.trim().toLowerCase().replace(/_/g, "-");
  if (normalized === "financialaid") {
    return "financial-aid";
  }
  return (ALL_PARENT_MODULE_IDS as readonly string[]).includes(normalized)
    ? (normalized as ParentPortalModuleId)
    : null;
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const readEnabledFlag = (value: unknown): boolean | null => {
  if (typeof value === "boolean") {
    return value;
  }
  const record = asRecord(value);
  if (!record) {
    return null;
  }
  if (typeof record.enabled === "boolean") {
    return record.enabled;
  }
  if (typeof record.visible === "boolean") {
    return record.visible;
  }
  if (typeof record.disabled === "boolean") {
    return !record.disabled;
  }
  return null;
};

const findParentPortalGovernance = (payload: unknown): unknown => {
  const root = asRecord(payload);
  const governance = asRecord(root?.governance);
  return (
    governance?.parentPortal ??
    governance?.parent ??
    root?.parentPortalModules ??
    root?.parentModules ??
    null
  );
};

const readConfiguredEnabledIds = (
  payload: unknown,
): Set<ParentPortalModuleId> | null => {
  const parentGovernance = findParentPortalGovernance(payload);
  const record = asRecord(parentGovernance);
  const modules = record?.modules ?? parentGovernance;

  if (Array.isArray(modules)) {
    const enabled = new Set<ParentPortalModuleId>();
    let sawRecognizedModule = false;
    for (const entry of modules) {
      const moduleRecord = asRecord(entry);
      const id = normalizeModuleId(
        moduleRecord?.id ?? moduleRecord?.moduleId ?? moduleRecord?.key,
      );
      if (!id) {
        continue;
      }
      sawRecognizedModule = true;
      if (readEnabledFlag(moduleRecord) !== false) {
        enabled.add(id);
      }
    }
    return sawRecognizedModule ? enabled : null;
  }

  const moduleRecord = asRecord(modules);
  if (!moduleRecord) {
    return null;
  }

  const enabled = new Set<ParentPortalModuleId>();
  let sawRecognizedModule = false;
  for (const [key, value] of Object.entries(moduleRecord)) {
    const id = normalizeModuleId(key);
    if (!id) {
      continue;
    }
    sawRecognizedModule = true;
    if (readEnabledFlag(value) !== false) {
      enabled.add(id);
    }
  }
  return sawRecognizedModule ? enabled : null;
};

export const resolveEffectiveParentPortalModules = ({
  settingsPayload,
  engineeringAvailableIds = ENGINEERING_AVAILABLE_PARENT_MODULE_IDS,
}: {
  settingsPayload: unknown;
  engineeringAvailableIds?: ReadonlyArray<ParentPortalModuleId>;
}): ParentPortalModuleAvailability => {
  const configuredEnabledIds = readConfiguredEnabledIds(settingsPayload);
  const availableIds = new Set(engineeringAvailableIds);
  const enabledIds = configuredEnabledIds ?? new Set(availableIds);
  const navIds = ALL_PARENT_MODULE_IDS.filter(
    (id) => availableIds.has(id) && enabledIds.has(id),
  );
  const disabledIds = ALL_PARENT_MODULE_IDS.filter((id) => !enabledIds.has(id));
  const descopedIds = ALL_PARENT_MODULE_IDS.filter(
    (id) => enabledIds.has(id) && !availableIds.has(id),
  );

  return { navIds, disabledIds, descopedIds };
};

export const resolveEffectiveParentPortalNavIds = (
  enabledNavIds: ReadonlySet<string>,
  engineeringAvailableIds: ReadonlyArray<ParentPortalModuleId> = ENGINEERING_AVAILABLE_PARENT_MODULE_IDS,
): Set<string> => {
  const availableIds = new Set(engineeringAvailableIds);
  return new Set(
    ALL_PARENT_MODULE_IDS.filter(
      (id) => availableIds.has(id) && enabledNavIds.has(id),
    ),
  );
};

export const isParentPortalModuleVisible = (
  visibleNavIds: ReadonlySet<string>,
  moduleId: ParentPortalModuleId,
): boolean => visibleNavIds.has(moduleId);

/**
 * Keeps Financial Aid invite focus from reintroducing modules that settings
 * governance has disabled.
 */
export const resolveParentFinancialAidFocusedNavOptions = (
  enabledParentNavIds: ReadonlySet<string>,
  focusActive: boolean,
): { includeDashboard: boolean; includeFinancialAid: boolean } => ({
  includeDashboard: Boolean(
    focusActive && enabledParentNavIds.has("dashboard"),
  ),
  includeFinancialAid: Boolean(
    focusActive && enabledParentNavIds.has("financial-aid"),
  ),
});

export const resolveParentPortalModuleIdFromPath = (
  pathname: string,
): ParentPortalModuleId | null => {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  const entries = Object.entries(PARENT_MODULE_ROUTE_MAP)
    .map(([id, route]) => ({ id: id as ParentPortalModuleId, route }))
    .sort((left, right) => right.route.length - left.route.length);
  const match = entries.find(({ id, route }) => {
    const candidates = [route, ...(PARENT_MODULE_ROUTE_ALIASES[id] ?? [])];
    return candidates.some(
      (candidate) =>
        normalized === candidate || normalized.startsWith(`${candidate}/`),
    );
  });
  return match?.id ?? null;
};

export const resolveParentPortalFallbackPath = (
  pathname: string,
  availability: ParentPortalModuleAvailability,
): string | null => {
  const currentModuleId = resolveParentPortalModuleIdFromPath(pathname);
  if (currentModuleId && availability.navIds.includes(currentModuleId)) {
    return pathname;
  }
  const fallbackId = availability.navIds[0];
  return fallbackId ? PARENT_MODULE_ROUTE_MAP[fallbackId] : null;
};

/**
 * Resolves whether a parent module route may render its content for the current
 * settings-backed visible nav set.
 */
export const resolveParentPortalModuleGateState = ({
  pathname,
  visibleNavIds,
  settingsPending,
}: {
  pathname: string;
  visibleNavIds: ReadonlySet<string>;
  settingsPending: boolean;
}): ParentPortalModuleGateState => {
  const moduleId = resolveParentPortalModuleIdFromPath(pathname);
  if (!moduleId) {
    return {
      status: "unmatched",
      moduleId: null,
      fallbackPath: null,
      shouldRenderModuleContent: true,
    };
  }

  if (settingsPending) {
    return {
      status: "pending",
      moduleId,
      fallbackPath: null,
      shouldRenderModuleContent: false,
    };
  }

  if (isParentPortalModuleVisible(visibleNavIds, moduleId)) {
    return {
      status: "enabled",
      moduleId,
      fallbackPath: null,
      shouldRenderModuleContent: true,
    };
  }

  const fallbackId = ALL_PARENT_MODULE_IDS.find((id) => visibleNavIds.has(id));
  return {
    status: "disabled",
    moduleId,
    fallbackPath: fallbackId ? PARENT_MODULE_ROUTE_MAP[fallbackId] : null,
    shouldRenderModuleContent: false,
  };
};
